import React, { useState } from "react";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";

import { Card, FormGroup } from "@cofano/cck";

import { Alert, Box, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import LinkOffSharpIcon from "@mui/icons-material/LinkOffSharp";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileContract } from "@fortawesome/pro-duotone-svg-icons";

import { useConfirm } from "components/hooks/useConfirm";
import ContractStatusChanger from "components/contract/ContractStatusChanger";

import ContractCreateEditDialog from "view/contractsNew/components/dialogs/ContractCreateEditDialog";
import CharterTypeTag from "view/charters/components/tags/CharterTypeTag";

import { DataFormat, Language, usePermission } from "imports/Utils";

import { DetailsBlock, Height } from "../Blocks";

import { gql } from "__generated__/gql";
import { Contract, PurchaseCharter, SalesCharter } from "types/index";

import { ContractLink, DossierLink, RelationLink } from "utils/LinkUtils";
import { FormattedDateWindow } from "utils/DateUtils";
import { ChevronRight } from "@mui/icons-material";
import CharterSubtypeTag from "view/charters/components/tags/CharterSubtypeTag";
import { CharterSubtype, HistoryEntity } from "__generated__/enums";
import { PurchaseCharterFromQuery } from "view/charters/PurchaseCharterDetails";
import { CharterContractFragment } from "__generated__/view/contractsNew/graphql/contract.fragments.generated";
import HistoryIcon from "@mui/icons-material/History";
import EntityHistoryDialog from "components/entityHistory/components/dialogs/EntityHistoryDialog.tsx";

type ContractDetailsBlock = {
	charter: SalesCharter | PurchaseCharter | PurchaseCharterFromQuery;
	contracts: Contract[] | CharterContractFragment[];
	color: string;
	refetch: () => void;
	onCreateOrLinkContract: () => void;
	isOtherCharter: boolean;
};

const ContractDetailsBlock = ({ charter, contracts, color, refetch, onCreateOrLinkContract, isOtherCharter }: ContractDetailsBlock) => {

	const confirm = useConfirm();

	const { isReadOnly } = usePermission();

	const [ selectedIndex, selectIndex ] = useState(0);
	const [ dialog, setDialog ] = useState<{ open: boolean, contractId?: number }>({ open: false, contractId: undefined });
	const [ historyDialogOpen, openHistoryDialog ] = useState<boolean>(false);

	const [ unlinkContract, { loading: unlinkContractLoading } ] = useMutation(UNLINK_CONTRACT_MUTATION);
	const [ unlinkSupplierCharter, { loading: unlinkSupplierCharterLoading } ] = useMutation(UNLINK_SUPPLIER_CHARTER_MUTATION);

	const contract = contracts[selectedIndex];
	const loading = unlinkContractLoading || unlinkSupplierCharterLoading;


	const unlink = () => {
		if (contract.charterSubtype === CharterSubtype.Supplier) {
			unlinkSupplierCharter({
				variables: {
					supplierCharterId: contract.charters?.[0]?.id as number,
					suppliedCharterId: charter.id,
				},
			}).then(() => {
				selectIndex(0);
				refetch();
			});
		} else {
			unlinkContract({
				variables: {
					contractId: contract.id,
					charterId: charter.id,
				},
			}).then(() => {
				selectIndex(0);
				refetch();
			});
		}
	};

	return (
		<React.Fragment>
			<DetailsBlock height={Height.Auto}>
				<Card
					collapsable={true}
					defaultCollapsed={false}
					color={color}
					icon={<FontAwesomeIcon icon={faFileContract} style={{ color: color, height: 22, width: 22 }} />}
					title={<Box sx={{ display: "flex", alignItems: "center" }}>
						{contract.parentContract && (
							<>
								<ContractLink contract={contract.parentContract} />
								<ChevronRight color="primary"  />
							</>
						)}
						<Box marginLeft={2}>
							<ContractLink contract={contract} />
						</Box>
					</Box>}
					actions={[ {
						icon: <EditIcon fontSize="small" />,
						hidden: isReadOnly,
						onClick: () => setDialog({ open: true, contractId: contract.id }),
						disabled: loading || !contract.isEditable,
					}, {
						icon: <LinkOffSharpIcon fontSize="small" />,
						hidden: isReadOnly,
						disabled: contract.status?.name === "FINISHED" || loading,
						onClick: () => {
							confirm({
								type: "WARNING",
								title: intl.formatMessage({ id: "CONFIRM_UNLINK_CHARTER" }),
							}).then(() => {
								unlink();
							});
						},
					}, {
						icon: <HistoryIcon fontSize="small" />,
						tooltip: intl.formatMessage({ id: "HISTORY" }),
						onClick: () => openHistoryDialog(true),
					} ]}
					disablePadding>
					<Box sx={{ borderTop: 1, borderColor: "divider", display: "flex", height: "100%" }}>
						<div>
							<EntityList
								entities={contracts.map(it => ({
									id: it.id || 0,
									name: it.contractNumber || "",
								}))}
								selectedIndex={selectedIndex}
								onSelect={index => selectIndex(index)}
								onAdd={() => onCreateOrLinkContract()} />
						</div>

						<div style={{ padding: 16, width: "100%" }}>
							{"hasApplicableContract" in charter && !charter.hasApplicableContract && (
								<Alert severity="warning" sx={{ mb: 2 }}>{intl.formatMessage({ id: "INCOMPATIBLE_CONTRACT_WARNING" })}</Alert>
							)}

							<FormGroup label={Language("TYPE")} labelPlacement="left-center" overflow="hidden">
								<CharterTypeTag type={contract.charterType} />
							</FormGroup>

							<FormGroup label={Language("SUBTYPE")} labelPlacement="left-center" overflow="hidden">
								<CharterSubtypeTag type={contract.charterSubtype} />
							</FormGroup>

							<FormGroup label={Language("STATUS")} labelPlacement="left-center" overflow="hidden">
								{contract.status && (
									<ContractStatusChanger
										contract={{
											...contract,
											status: contract.status,
										}}
										refetch={refetch}
									/>
								)}
							</FormGroup>

							<FormGroup label={Language("DESCRIPTION")} labelPlacement="left-center" overflow="hidden">
								<Typography
									sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "small" }}>
									{contract.description}
								</Typography>
							</FormGroup>

							{contract.termsAndConditions && (
								<FormGroup label={Language("TERMS_AND_CONDITIONS")} labelPlacement="left-center" overflow="hidden">
									<Typography
										sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "small" }}>
										{contract.termsAndConditions.description}
									</Typography>
								</FormGroup>
							)}

							{isOtherCharter && (
								<FormGroup label={Language("INVOICE_CREDITOR")} labelPlacement="left-center" overflow="hidden">
									<RelationLink relation={contract.creditor} />
								</FormGroup>
							)}

							{!isOtherCharter && (
								<FormGroup label={Language("INVOICE_DEBTOR")} labelPlacement="left-center" overflow="hidden">
									<RelationLink relation={contract.debtor} />
								</FormGroup>
							)}

							{contract.validFrom != null && (<FormGroup label={Language("VALID_BETWEEN")} labelPlacement="left-center" overflow="hidden">
								<FormattedDateWindow from={contract.validFrom} to={contract.validUntil} format="ll" />
							</FormGroup>)}

							{isOtherCharter && (
								<FormGroup label={Language("CHARTER")} labelPlacement="left-center" overflow="hidden">
									<DossierLink charter={charter} />
								</FormGroup>
							)}

							{!isOtherCharter && (
								<FormGroup label={Language("INCLUDE_PRICING_IN_NOMINATION")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.boolean(contract.includePricingInNomination)}
								</FormGroup>
							)}
						</div>
					</Box>
				</Card>
			</DetailsBlock>

			<ContractCreateEditDialog
				open={dialog.open}
				onClose={() => setDialog({ open: false, contractId: undefined })}
				charter={charter}
				contractId={dialog.contractId}
				refetch={() => refetch()} />

			<EntityHistoryDialog
				title={intl.formatMessage({ id: "HISTORY" })}
				open={historyDialogOpen}
				onClose={() => openHistoryDialog(false)}
				entity={HistoryEntity.Contract}
				entityId={contract.id}
			/>
		</React.Fragment>
	);
};

const EntityList = ({ entities, selectedIndex, onSelect, onAdd }: {
	entities: { id?: number, name?: string }[];
	selectedIndex: number;
	onSelect: (index: number) => void;
	onAdd: () => void;
}) => {

	const { isReadOnly } = usePermission();

	return (
		<div className="entity-list">
			{entities.map((entity, index) => (
				<div
					key={index}
					className={`list-item ${selectedIndex === index ? "selected" : ""}`}
					onClick={() => onSelect(index)}>
					{entity.name}
				</div>
			))}

			{!isReadOnly && (
				<div className="add-item" onClick={() => onAdd()}>
					<AddIcon fontSize="small" />
				</div>
			)}
		</div>
	);
};

const UNLINK_CONTRACT_MUTATION = gql(`#graphql
	mutation unlinkContract_contractDetailsBlock($contractId: Long!, $charterId: Long!) {
		unlinkContract(charterId: $charterId, contractId: $contractId) {
			id
		}
	}
`);

const UNLINK_SUPPLIER_CHARTER_MUTATION = gql(`#graphql
	mutation unlinkSupplierCharter_contractDetailsBlock($supplierCharterId: Long!, $suppliedCharterId: Long!) {
		unlinkSupplierCharter(supplierCharterId: $supplierCharterId, suppliedCharterId: $suppliedCharterId)
	}
`);

export default ContractDetailsBlock;
